import * as React from 'react';
import { ChangeEvent } from 'react';
import { Field, Form } from 'react-final-form';

import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';

import RefundPaper from '../RefundPaper/RefundPaper';
import PhoneInput from '../../../PhoneInput';
import { FormControlLabel } from '@mui/material';
import { useRequestFormValidation } from './hooks';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../theme';
import Input from '../../../Input';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { getSelectedPassengers } from '../../store/passengers/selectors';
import InfoLinksLabel from '../../../InfoLinksLabel';
import { getAviaOrderTravellers, getCustomer } from '../../store/aviaOrder/selectors';
import { getTravellerInfo } from '../../store/aviaOrder/utils';
import { RefundState } from '../../types';
import { getOrder, getSelectedInsurancesByType } from '../../store/selectors';
import { InsuranceType } from '@websky/graphql';
import { RefundSummary } from '../../../RefundSummary';
import AirportServices from '../Pages/AirportServices';

export interface RequestFormState {
	name: string;
	email: string;
	phone: string;
	agree: boolean;

	comments?: string;
}

export interface Props {
	onSubmit: React.Dispatch<RequestFormState>;
	renderButtons: (isFormValid: boolean, onSubmit: React.DispatchWithoutAction) => React.ReactElement;
	renderPrice?: () => React.ReactElement;
	renderNotes?: () => React.ReactElement;
	onBackToOrder?: () => void;
	termsOfServiceURL?: string;
	className?: string;
	isNasServiceRefundRequired?: boolean;
	onNasServicesSuccessfullyRefunded?: () => void;
	isRulesOptional?: boolean;
}

const RequestForm: React.FC<Props> = props => {
	const { t } = useTranslation('Refund');
	const { RequestForm: css } = useTheme('Refund');
	const { validateEmail, validateAgreeButton, validateComments, validateName } = useRequestFormValidation();

	const order = useSelector(getOrder);
	const selectedPassengers = useSelector(getSelectedPassengers);
	const travellers = useSelector(getAviaOrderTravellers);
	const refundState = useSelector<RefundState, RefundState>(state => state);
	const customer = useSelector(getCustomer);
	const selectedVskInsurances = useSelector(getSelectedInsurancesByType(InsuranceType.Vsk));

	const travellerInfo = React.useMemo(() => {
		return getTravellerInfo(
			customer,
			travellers,
			selectedPassengers.map(p => p.id)
		);
	}, [customer, travellers, selectedPassengers]);

	return (
		<div className={css.wrapper}>
			{props.renderPrice && props.renderPrice()}

			<div className={css.notes}>{props.renderNotes && props.renderNotes()}</div>

			{props.isNasServiceRefundRequired && (
				<AirportServices
					onRefuse={props.onBackToOrder}
					onSuccessfullyRefunded={props.onNasServicesSuccessfullyRefunded}
				/>
			)}

			{!props.isNasServiceRefundRequired && (
				<>
					{selectedVskInsurances.length > 0 && (
						<RefundSummary
							className={css.selectedServices}
							flight={order.flight}
							travellers={order.travellers}
							insurancesRefund={selectedVskInsurances}
						/>
					)}

					<Form<RequestFormState> onSubmit={props.onSubmit}>
						{({ handleSubmit, valid, errors }) => (
							<>
								<RefundPaper className={cn(css.form, props.className)}>
									<form onSubmit={handleSubmit}>
										<FormGroup>
											<FormLabel className={css.formLabel}>{t('Create request')}</FormLabel>

											<Field
												name={'name'}
												initialValue={travellerInfo.name}
												type={'string'}
												validate={validateName}
												render={({ input, meta }) => (
													<Input
														className={css.formName}
														absoluteHintPosition={true}
														TextFieldProps={{
															...input,
															classes: { root: css.control },
															error: !!(meta.touched && meta.error),
															helperText: meta.touched && t(meta.error)
														}}
														label={t('Your name')}
													/>
												)}
											/>
											<Field
												name={'email'}
												initialValue={travellerInfo.email}
												type={'string'}
												validate={validateEmail}
												render={({ input, meta }) => (
													<Input
														className={css.formEmail}
														TextFieldProps={{
															...input,

															classes: { root: css.control },
															error: !!(meta.error && meta.touched),
															helperText: meta.touched && t(meta.error)
														}}
														label={t('Your email')}
													/>
												)}
											/>
											<Field
												name={'phone'}
												type={'string'}
												initialValue={travellerInfo.phone}
												render={({ input, meta }) => (
													<PhoneInput
														className={cn(css.control, css.formTel)}
														{...input}
														textFieldsProps={{
															error: !!(meta.error && meta.touched),
															helperText: meta.touched && t(meta.error),
															label: t('Your phone'),
															fullWidth: true,

															onChange: (event: ChangeEvent<HTMLInputElement>) => {
																input.onChange(event);
															}
														}}
													/>
												)}
											/>
											<Field
												name={'comments'}
												validate={validateComments}
												type={'string'}
												render={({ input, meta }) => (
													<Input
														className={css.formComment}
														TextFieldProps={{
															...input,
															classes: { root: css.control },
															error: !!(meta.error && meta.touched),
															helperText: meta.touched && t(meta.error)
														}}
														label={t('Your comments')}
													/>
												)}
											/>
										</FormGroup>
									</form>
								</RefundPaper>

								{!props.isRulesOptional && (
									<Field
										name={'agree'}
										type={'checkbox'}
										validate={validateAgreeButton}
										render={({ input }) => {
											const { type, ...restInputProps } = input;

											return (
												<FormControlLabel
													className={css.agree}
													control={
														<Checkbox
															classes={{
																root: css.checkbox,
																checked: cn(css.checkbox, css.active)
															}}
															{...restInputProps}
														/>
													}
													label={<InfoLinksLabel order={refundState} type={'refund'} />}
												/>
											);
										}}
									/>
								)}

								{props.renderButtons && props.renderButtons(valid, handleSubmit)}
							</>
						)}
					</Form>
				</>
			)}
		</div>
	);
};

export default RequestForm;
